<template>
  <div
    ref="editorRef"
    :id="id"
    :contenteditable="true"
    :class="['rich-text-editor', { 'drag-over': isOver, 'single-line': singleLine }]"
    :style="{ minHeight: minHeight }"
    :data-field-id="id"
    @input="handleInput($event)"
    @drop="handleDrop"
    @dragover.prevent="handleDragOver"
    @dragenter.prevent="handleDragEnter"
    @dragleave="handleDragLeave"
    @keydown="handleKeyDown"
    @paste="handlePaste"
    @focus="isFocused = true"
    @blur="handleBlur"
    :data-placeholder="placeholder"
  ></div>
</template>

<script>
export default {
  name: 'RichTextFieldInput',
  emits: ['input', 'change', 'blur'],
  props: {
    id: {
      type: String,
      required: true
    },
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    isOver: {
      type: Boolean,
      default: false
    },
    minHeight: {
      type: String,
      default: '80px'
    },
    singleLine: {
      type: Boolean,
      default: false
    },
    exampleData: {
      type: Object,
      default: null
    },
    showExamples: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isInternalUpdate: false,
      lastValue: '',
      isFocused: false
    };
  },
  watch: {
    value(newVal) {
      // Convert objects to empty string, filter out event objects and '[object InputEvent]' strings
      let stringVal = '';
      if (typeof newVal === 'string') {
        stringVal = newVal;
      } else if (newVal !== null && newVal !== undefined) {
        // If it's an object, convert to empty string
        stringVal = '';
        console.warn('RichTextFieldInput received non-string value:', newVal);
      }
      
      // If the component is focused, we assume the local state is the most up-to-date
      // We only update if the value from the parent is significantly different 
      // from what we currently have in the editor.
      if (this.isFocused) {
        const editor = this.$refs.editorRef;
        if (editor) {
          const currentContent = this.getTextContent(editor);
          // Only update if the parent value is actually different from what's on screen
          if (stringVal === currentContent) {
            this.lastValue = stringVal;
            return;
          }

          // Protect against stale updates from the parent (e.g. debounced saves)
          // If the parent is sending back an older/shorter version of what we already have, ignore it.
          if (this.lastValue.length > stringVal.length && this.lastValue.startsWith(stringVal)) {
            return;
          }
        }
      }

      if (stringVal !== this.lastValue && !this.isInternalUpdate && stringVal !== '[object InputEvent]' && !stringVal.includes('[object')) {
        this.updateContent(stringVal);
        this.lastValue = stringVal;
      }
    },
    isOver(newVal) {
      if (newVal && this.$refs.editorRef) {
        // Focus when dragging over to position cursor
        this.$refs.editorRef.focus();
      }
    },
    showExamples() {
      // When showExamples changes, re-render the content
      if (this.$refs.editorRef) {
        // Always render, ensure we pass a string
        let valueToRender = this.value;
        if (typeof valueToRender !== 'string') {
          valueToRender = (this.lastValue && typeof this.lastValue === 'string') ? this.lastValue : '';
        }
        this.renderContent(valueToRender);
      }
    },
    exampleData() {
      // When exampleData changes, re-render if showing examples
      if (this.showExamples && this.$refs.editorRef) {
        let valueToRender = this.value;
        if (typeof valueToRender !== 'string') {
          valueToRender = (this.lastValue && typeof this.lastValue === 'string') ? this.lastValue : '';
        }
        this.renderContent(valueToRender);
      }
    }
  },
  mounted() {
    // Ensure value is a string
    const stringValue = (typeof this.value === 'string') ? this.value : '';
    this.updateContent(stringValue);
    this.lastValue = stringValue;
    
    // Listen for custom event to insert merge field at cursor
    window.addEventListener('insert-merge-field-at-cursor', this.handleInsertField);
    
    // Track mouse position during drag
    document.addEventListener('dragover', this.trackMousePosition);
  },
  beforeUnmount() {
    window.removeEventListener('insert-merge-field-at-cursor', this.handleInsertField);
    document.removeEventListener('dragover', this.trackMousePosition);
  },
  methods: {
    updateContent(value) {
      if (!this.$refs.editorRef) return;
      
      const editor = this.$refs.editorRef;
      const currentContent = this.getTextContent(editor);
      
      if (currentContent !== value) {
        this.isInternalUpdate = true;
        this.renderContent(value);
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
    },
    renderContent(value) {
      if (!this.$refs.editorRef) return;
      
      const editor = this.$refs.editorRef;
      
      // Ensure value is a string to prevent .trim() and other string method errors
      let valueToRender = value;
      if (typeof valueToRender !== 'string') {
        if (valueToRender === null || valueToRender === undefined) {
          valueToRender = '';
        } else {
          // If it's an object (like InputEvent), try to use the last known good string value
          valueToRender = (this.lastValue && typeof this.lastValue === 'string') ? this.lastValue : '';
        }
      }
      
      // If showExamples is true and exampleData exists, show preview values IN the buttons
      if (this.showExamples && this.exampleData && typeof this.exampleData === 'object') {
        
        // If value is empty, show a blank button with 1 character (space)
        if (!valueToRender || valueToRender.trim() === '') {
          editor.innerHTML = '<span class="merge-field-button" contenteditable="false" style="opacity: 0.5; min-width: 20px; display: inline-block;">&nbsp;</span>';
          return;
        }
        
        // ... (rest of the preview logic)
        // Parse merge fields and create buttons with preview values
        const mergeFieldRegex = /\{([^}]+)\}/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = mergeFieldRegex.exec(valueToRender)) !== null) {
          // Add text before the merge field
          if (match.index > lastIndex) {
            parts.push({
              type: 'text',
              content: valueToRender.substring(lastIndex, match.index)
            });
          }
          
          // Add merge field button with preview value
          const fieldKey = match[1];
          const exampleValue = this.getExampleValue(fieldKey);
          // Show space if blank, otherwise show the example value
          const displayValue = (exampleValue !== null && exampleValue !== undefined && String(exampleValue).trim() !== '') 
            ? String(exampleValue) 
            : ' '; // Show space (spacer) instead of field key when blank 
          
          parts.push({
            type: 'field',
            fieldKey: fieldKey,
            displayValue: displayValue,
            content: match[0]
          });
          
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        if (lastIndex < valueToRender.length) {
          parts.push({
            type: 'text',
            content: valueToRender.substring(lastIndex)
          });
        }
        
        // If no merge fields, just set text
        if (parts.length === 0) {
          editor.textContent = valueToRender;
          return;
        }
        
        // Clear and rebuild content with buttons showing preview values
        editor.innerHTML = '';
        
        parts.forEach(part => {
          if (part.type === 'text') {
            const textNode = document.createTextNode(part.content);
            editor.appendChild(textNode);
          } else if (part.type === 'field') {
            // Create button with preview value displayed
            const button = document.createElement('span');
            button.className = 'merge-field-button';
            button.contentEditable = 'false';
            // Use non-breaking space for blank fields to maintain proper alignment
            const displayText = part.displayValue.trim() === '' ? '\u00A0' : part.displayValue;
            button.textContent = displayText;
            button.setAttribute('data-field-key', part.fieldKey);
            button.setAttribute('draggable', 'true');
            button.setAttribute('title', `{${part.fieldKey}}`); // Show original field key in tooltip
            
            // Make button draggable
            button.addEventListener('dragstart', (e) => {
              e.dataTransfer.setData('text/plain', `{${part.fieldKey}}`);
              e.dataTransfer.setData('application/merge-field', part.fieldKey);
              e.dataTransfer.effectAllowed = 'move';
              button.style.opacity = '0.5';
            });
            
            button.addEventListener('dragend', () => {
              button.style.opacity = '1';
            });
            
            editor.appendChild(button);
          }
        });
        
        return;
      }
      
      // If value is empty and NOT showing examples, make it truly empty
      // This allows clicking into the field and shows the placeholder
      if (!valueToRender || valueToRender.trim() === '') {
        editor.innerHTML = '';
        return;
      }
      
      // Parse merge fields like {fieldKey}
      const mergeFieldRegex = /\{([^}]+)\}/g;
      const parts = [];
      let lastIndex = 0;
      let match;
      
      while ((match = mergeFieldRegex.exec(valueToRender)) !== null) {
        // Add text before the merge field
        if (match.index > lastIndex) {
          parts.push({
            type: 'text',
            content: valueToRender.substring(lastIndex, match.index)
          });
        }
        
        // Add merge field button
        parts.push({
          type: 'field',
          fieldKey: match[1],
          content: match[0]
        });
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < valueToRender.length) {
        parts.push({
          type: 'text',
          content: valueToRender.substring(lastIndex)
        });
      }
      
      // If no merge fields, just set text
      if (parts.length === 0) {
        editor.textContent = valueToRender;
        return;
      }
      
      // Clear and rebuild content
      editor.innerHTML = '';
      
      parts.forEach(part => {
        if (part.type === 'text') {
          const textNode = document.createTextNode(part.content);
          editor.appendChild(textNode);
        } else if (part.type === 'field') {
          const button = document.createElement('span');
          button.className = 'merge-field-button';
          button.contentEditable = 'false';
          button.textContent = part.fieldKey;
          button.setAttribute('data-field-key', part.fieldKey);
          button.setAttribute('draggable', 'true');
          
          // Make button draggable
          button.addEventListener('dragstart', (e) => {
            e.dataTransfer.setData('text/plain', `{${part.fieldKey}}`);
            e.dataTransfer.setData('application/merge-field', part.fieldKey);
            e.dataTransfer.effectAllowed = 'move';
            button.style.opacity = '0.5';
          });
          
          button.addEventListener('dragend', () => {
            button.style.opacity = '1';
          });
          
          editor.appendChild(button);
        }
      });
    },
    getExampleValue(fieldKey) {
      if (!this.exampleData || typeof this.exampleData !== 'object') {
        return null;
      }
      
      const data = this.exampleData;
      const parts = fieldKey.split('.');
      
      // Helper to format value
      const formatValue = (val) => {
        if (!val) return val;
        if (Array.isArray(val)) {
          return val.map(v => formatValue(v)).join(', ');
        }
        if (typeof val === 'string' && val.startsWith('http')) {
          try {
            const url = new URL(val);
            return url.pathname.split('/').pop() || val;
          } catch (e) {
            return val;
          }
        }
        return val;
      };
      
      // Handle custom field mapping FIRST (before standard field checks)
      // Custom fields have keys like "opportunity.{customFieldId}" or "contact.{customFieldId}"
      if (parts[0] === 'opportunity' && parts.length === 2 && parts[1]) {
        const fieldKeyPart = parts[1];
        // Get opportunity data (might be nested or at top level)
        // The data structure can be either:
        // 1. { opportunity: {...}, contact: {...} } - nested
        // 2. { ...opportunityData, contact: {...} } - spread at top level
        const oppData = data.opportunity || data;
        
        // ALWAYS check custom fields first for opportunity fields
        const oppCustomFields = oppData.customFields || [];
        if (oppCustomFields.length > 0) {
          const customField = oppCustomFields.find(f => f.id === fieldKeyPart);
          if (customField) {
            // Handle both value and fieldValue properties, and array fieldValue
            if (Array.isArray(customField.fieldValue)) {
              return formatValue(customField.fieldValue.map(function(f) { return (f.meta && f.meta.name) || f.url || 'File'; }).join(', '));
            }
            return formatValue(customField.value || customField.fieldValue);
          }
        }
        
        // If not a custom field, try standard field access
        // Check both the opportunity object (if nested) and top-level data
        let val = oppData[fieldKeyPart];
        // If oppData is data.opportunity (nested), we already have the right value
        // If oppData is data (spread), we also have the right value
        // But if val is undefined and we're using nested structure, also check top level
        if (val === undefined && data.opportunity && data !== oppData) {
          val = data[fieldKeyPart];
        }
        // Also check if the field is at the top level when data structure is spread
        if (val === undefined && !data.opportunity) {
          val = data[fieldKeyPart];
        }
        
        if (val !== undefined) {
          // Handle null - return null (not empty string)
          if (val === null) {
            return null;
          }
          // If it's a Date object, format it
          if (val instanceof Date) {
            return formatValue(val.toISOString());
          }
          // If it's a string, number, boolean, or array, return it
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean' || Array.isArray(val)) {
            return formatValue(val);
          }
          // If it's a plain object (not Date), skip it
          if (typeof val === 'object') {
            return null;
          }
          return formatValue(val);
        }
      } else if (parts[0] === 'contact' && parts.length === 2 && parts[1]) {
        const fieldKeyPart = parts[1];
        // Get contact data (might be nested in opportunity or at top level)
        const contactData = data.contact || (data.opportunity && data.opportunity.contact) || null;
        
        if (contactData) {
          // ALWAYS check custom fields first for contact fields
          const contactCustomFields = contactData.customFields || [];
          if (contactCustomFields.length > 0) {
            const customField = contactCustomFields.find(function(f) { return f.id === fieldKeyPart; });
            if (customField) {
              // Handle both value and fieldValue properties, and array fieldValue
              if (Array.isArray(customField.fieldValue)) {
                return formatValue(customField.fieldValue.map(function(f) { return f.meta && f.meta.name || f.url || 'File'; }).join(', '));
              }
              return formatValue(customField.value || customField.fieldValue);
            }
          }
          
          // If not a custom field, try standard field access (but skip if it's an object/array)
          const val = contactData[fieldKeyPart];
          if (val !== undefined && typeof val !== 'object') {
            return formatValue(val);
          }
        }
      } else if (parts[0] === 'company') {
        if (parts[1] === 'name') return data.contact?.companyName || null;
        if (parts[1] === 'address') return data.contact?.address1 || null;
      } else if (parts[0] === 'servicem8') {
        // Handle ServiceM8 formatted fields
        if (parts[1] === 'client') {
          const contactData = data.contact || (data.opportunity && data.opportunity.contact) || null;
          if (contactData) {
            // If companyName is not blank, use it; otherwise use firstName + lastName
            const companyName = contactData.companyName;
            if (companyName && String(companyName).trim() !== '') {
              return formatValue(companyName);
            } else {
              // Concatenate firstName and lastName
              const firstName = contactData.firstName || '';
              const lastName = contactData.lastName || '';
              const fullName = `${firstName} ${lastName}`.trim();
              return fullName || null;
            }
          }
          return null;
        } else if (parts[1] === 'jobAddress') {
          const contactData = data.contact || (data.opportunity && data.opportunity.contact) || null;
          if (contactData) {
            const companyName = contactData.companyName;
            let addressParts = [];
            
            if (companyName && String(companyName).trim() !== '') {
              // Use company address fields
              const address1 = contactData.company?.address1 || contactData.address1 || '';
              const city = contactData.company?.city || contactData.city || '';
              const state = contactData.company?.state || contactData.state || '';
              const postcode = contactData.company?.postcode || contactData.postcode || contactData.postalCode || '';
              
              if (address1) addressParts.push(address1);
              const cityStatePostcode = [city, state, postcode].filter(v => v).join(' ');
              if (cityStatePostcode) addressParts.push(cityStatePostcode);
            } else {
              // Use contact address fields
              const address1 = contactData.address1 || '';
              const city = contactData.city || '';
              const state = contactData.state || '';
              const postcode = contactData.postcode || contactData.postalCode || '';
              
              if (address1) addressParts.push(address1);
              const cityStatePostcode = [city, state, postcode].filter(v => v).join(' ');
              if (cityStatePostcode) addressParts.push(cityStatePostcode);
            }
            
            // Join with newline
            return addressParts.length > 0 ? addressParts.join('\n') : null;
          }
          return null;
        } else if (parts[1] === 'mobile') {
          return this.formatPhoneNumber(this.getMobileNumber(data));
        } else if (parts[1] === 'phone') {
          return this.formatPhoneNumber(this.getPhoneNumber(data));
        }
      } else if (parts[0] === 'custom_values') {
        // Note: customValues would need to be passed separately or accessed differently
        // For now, return null as we don't have access to customValues here
        return null;
      }
      
      // Standard traversal (only for non-custom-field keys)
      let value = data;
      for (const part of parts) {
        if (value && typeof value === 'object') {
          value = value[part];
        } else {
          return null;
        }
      }
      
      return value !== undefined ? formatValue(value) : null;
    },
    /**
     * Format Australian phone number from international format to local format
     * Mobile: +61403704197 -> 0403 704 197 (0XXX XXX XXX)
     * Landline: +61738034779 -> 07 3803 4779 (0X XXXX XXXX)
     * If number starts with +614, always format as mobile
     */
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber || typeof phoneNumber !== 'string') return null;
      
      // Remove all non-digit characters except +
      let cleaned = phoneNumber.trim();
      
      // Check if it's a mobile number (starts with +614)
      const isMobileNumber = cleaned.startsWith('+614');
      
      // If it's a mobile number, format as mobile regardless of isMobile parameter
      if (isMobileNumber) {
        // Remove +61 and add 0
        let digits = cleaned.replace(/^\+61/, '').replace(/\D/g, '');
        if (digits.length === 9) {
          // Format as 0XXX XXX XXX
          return `${digits[0]}${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
        }
      } else if (cleaned.startsWith('+617')) {
        // Landline number
        let digits = cleaned.replace(/^\+61/, '').replace(/\D/g, '');
        if (digits.length === 9) {
          // Format as 0X XXXX XXXX
          return `${digits[0]}${digits[1]} ${digits.slice(2, 6)} ${digits.slice(6)}`;
        }
      }
      
      // Fallback: return cleaned number if format doesn't match
      return cleaned.replace(/^\+61/, '0');
    },
    /**
     * Get mobile number from contact data
     * Priority: 1. phoneLabel === "Mobile" in contact, 2. First +614 number, 3. null
     */
    getMobileNumber(data) {
      const contact = data.contact || (data.opportunity && data.opportunity.contact) || null;
      if (!contact) return null;
      
      // First priority: Check main contact phone if phoneLabel is "Mobile"
      if (contact.phone && contact.phoneLabel === 'Mobile') {
        return contact.phone;
      }
      
      // Second priority: Check additionalPhones for Mobile
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const mobilePhone = contact.additionalPhones.find(p => p.phoneLabel === 'Mobile');
        if (mobilePhone && mobilePhone.phone) {
          return mobilePhone.phone;
        }
      }
      
      // Fallback: Check if main phone starts with +614 (mobile format)
      if (contact.phone && contact.phone.startsWith('+614')) {
        return contact.phone;
      }
      
      // Fallback: Check additionalPhones for any +614 number
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const mobileFormatPhone = contact.additionalPhones.find(p => p.phone && p.phone.startsWith('+614'));
        if (mobileFormatPhone && mobileFormatPhone.phone) {
          return mobileFormatPhone.phone;
        }
      }
      
      return null;
    },
    /**
     * Get phone number (Home/Work/Landline) from contact data
     * Priority: 1. First Home/Work/Landline in additionalPhones, 2. Main phone if not Mobile, 3. null
     */
    getPhoneNumber(data) {
      const contact = data.contact || (data.opportunity && data.opportunity.contact) || null;
      if (!contact) return null;
      
      const phoneLabels = ['Home', 'Work', 'Landline'];
      
      // First priority: Check additionalPhones for Home/Work/Landline
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const phoneNumber = contact.additionalPhones.find(p => 
          p.phone && phoneLabels.includes(p.phoneLabel)
        );
        if (phoneNumber && phoneNumber.phone) {
          return phoneNumber.phone;
        }
      }
      
      // Second priority: Check main contact phone if it's not Mobile
      if (contact.phone && contact.phoneLabel && phoneLabels.includes(contact.phoneLabel)) {
        return contact.phone;
      }
      
      // Fallback: If main phone is not Mobile and no Home/Work/Landline found, use main phone
      if (contact.phone && contact.phoneLabel !== 'Mobile') {
        return contact.phone;
      }
      
      return null;
    },
    getTextContent(element) {
      if (!element) return '';
      
      let text = '';
      const nodes = element.childNodes;
      
      for (const node of nodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          // Normalize non-breaking spaces to regular spaces
          text += node.textContent.replace(/\u00A0/g, ' ');
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains('merge-field-button')) {
            const fieldKey = node.getAttribute('data-field-key');
            if (fieldKey) {
              text += `{${fieldKey}}`;
            }
          } else if (node.tagName === 'BR') {
            text += '\n';
          } else if (node.tagName === 'DIV' || node.tagName === 'P') {
            // Get content of the block element
            const content = this.getTextContent(node);
            
            // If there's already text and we're starting a new block, add a newline
            if (text && !text.endsWith('\n')) {
              text += '\n';
            }
            
            text += content;
            
            // Ensure block ends with a newline
            if (!text.endsWith('\n')) {
              text += '\n';
            }
          } else {
            // For other elements (spans, etc.), just get their nested content
            text += this.getTextContent(node);
          }
        }
      }
      
      // Clean up multiple trailing newlines
      return text.replace(/\n+$/, '\n').trimEnd();
    },
    handleInput(event) {
      if (this.isInternalUpdate) return;
      
      // Stop native input event from bubbling up to parents
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
      
      // Get text content from the editor element, not the event
      const editor = this.$refs.editorRef;
      if (!editor) return;
      
      const text = this.getTextContent(editor);
      // Only emit if text actually changed and is a string (not an event object)
      // Also filter out '[object InputEvent]' string if it somehow got in
      if (text !== this.lastValue && typeof text === 'string' && text !== '[object InputEvent]' && !text.includes('[object')) {
        this.lastValue = text;
        
        // Temporarily block updates from the parent while we're emitting
        this.isInternalUpdate = true;
        this.$emit('input', text);
        this.$emit('change', text);
        
        // Release the block in the next tick after the parent has likely processed the change
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
    },
    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },
    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      
      // Get the field key from drag data
      let fieldKey = '';
      try {
        fieldKey = event.dataTransfer.getData('application/merge-field');
        if (!fieldKey || fieldKey === '') {
          const textData = event.dataTransfer.getData('text/plain');
          if (textData) {
            fieldKey = textData.replace(/[{}]/g, '');
          }
        }
      } catch (e) {
        console.error('Error getting drag data:', e);
      }
      
      if (fieldKey) {
        // Position cursor at drop point first
        this.positionCursorAtPoint(event.clientX, event.clientY);
        // Insert the field immediately (synchronously) so buttons render right away
        this.insertFieldAtCursor(`{${fieldKey}}`);
      }
    },
    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      
      // Position cursor at mouse position
      if (this.$refs.editorRef) {
        this.positionCursorAtPoint(event.clientX, event.clientY);
      }
    },
    handleDragEnter(event) {
      event.preventDefault();
      if (this.$refs.editorRef) {
        this.$refs.editorRef.classList.add('drag-over');
        this.$refs.editorRef.focus();
      }
    },
    handleDragLeave(event) {
      if (this.$refs.editorRef && !this.$refs.editorRef.contains(event.relatedTarget)) {
        this.$refs.editorRef.classList.remove('drag-over');
      }
    },
    handleKeyDown(event) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      
      // Handle backspace/delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        // If there's a selection, let the browser handle it normally
        if (!range.collapsed) {
          this.$nextTick(() => this.syncValue());
          return; 
        }
        
        const container = range.startContainer;
        const offset = range.startOffset;

        // BACKSPACE: Check if we are right AFTER a button
        if (event.key === 'Backspace') {
          // If cursor is at start of a text node, check previous sibling
          if (container.nodeType === Node.TEXT_NODE && offset === 0) {
            const prev = container.previousSibling;
            if (prev && prev.classList && prev.classList.contains('merge-field-button')) {
              event.preventDefault();
              prev.remove();
              this.syncValue();
              return;
            }
          }
          // If cursor is in the editor itself
          if (container === this.$refs.editorRef && offset > 0) {
            const nodeBefore = container.childNodes[offset - 1];
            if (nodeBefore && nodeBefore.classList && nodeBefore.classList.contains('merge-field-button')) {
              event.preventDefault();
              nodeBefore.remove();
              this.syncValue();
              return;
            }
          }
        }

        // DELETE: Check if we are right BEFORE a button
        if (event.key === 'Delete') {
          // If cursor is at end of a text node, check next sibling
          if (container.nodeType === Node.TEXT_NODE && offset === container.textContent.length) {
            const next = container.nextSibling;
            if (next && next.classList && next.classList.contains('merge-field-button')) {
              event.preventDefault();
              next.remove();
              this.syncValue();
              return;
            }
          }
          // If cursor is in the editor itself
          if (container === this.$refs.editorRef && offset < container.childNodes.length) {
            const nodeAfter = container.childNodes[offset];
            if (nodeAfter && nodeAfter.classList && nodeAfter.classList.contains('merge-field-button')) {
              event.preventDefault();
              nodeAfter.remove();
              this.syncValue();
              return;
            }
          }
        }
        
        // For normal text deletion, let the browser handle it naturally
        this.$nextTick(() => {
          this.syncValue();
        });
        return;
      }
      
      // Prevent Enter in single line mode
      if (this.singleLine && event.key === 'Enter') {
        event.preventDefault();
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      this.insertTextAtCursor(text);
    },
    insertFieldAtCursor(fieldText) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        // If no selection, insert at end
        if (this.$refs.editorRef) {
          const range = document.createRange();
          range.selectNodeContents(this.$refs.editorRef);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          return;
        }
      }
      
      let range = selection.getRangeAt(0);
      
      // Delete any selected content
      if (!range.collapsed) {
        range.deleteContents();
      }
      
      // Extract field key from fieldText (e.g., "{opportunity.name}" -> "opportunity.name")
      const fieldKeyMatch = fieldText.match(/\{([^}]+)\}/);
      const fieldKey = fieldKeyMatch ? fieldKeyMatch[1] : fieldText.replace(/[{}]/g, '');
      
      // Check if we're inside an existing merge field button - if so, insert after it
      let container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentNode;
      }
      
      // If we're inside a merge field button, insert after it
      if (container && container.classList && container.classList.contains('merge-field-button')) {
        const newRange = document.createRange();
        newRange.setStartAfter(container);
        newRange.collapse(true);
        range = newRange;
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Check if we need to add a space before the button
      const textBefore = range.startContainer.textContent || '';
      const offsetBefore = range.startOffset;
      if (offsetBefore > 0 && textBefore[offsetBefore - 1] !== ' ') {
        const spaceBefore = document.createTextNode(' ');
        range.insertNode(spaceBefore);
        range.setStartAfter(spaceBefore);
        range.collapse(true);
      }

      // Create the merge field button directly (don't insert as text first)
      const button = document.createElement('span');
      button.className = 'merge-field-button';
      button.contentEditable = 'false';
      button.textContent = fieldKey;
      button.setAttribute('data-field-key', fieldKey);
      button.setAttribute('draggable', 'true');
      
      // Make button draggable
      button.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData('text/plain', `{${fieldKey}}`);
        e.dataTransfer.setData('application/merge-field', fieldKey);
        e.dataTransfer.effectAllowed = 'move';
        button.style.opacity = '0.5';
      });
      
      button.addEventListener('dragend', () => {
        button.style.opacity = '1';
      });
      
      // Insert the button directly
      range.insertNode(button);
      
      // Move cursor after inserted button
      range.setStartAfter(button);
      range.collapse(true);

      // Check if we need to add a space after the button
      const textAfter = range.startContainer.textContent || '';
      const offsetAfter = range.startOffset;
      if (offsetAfter < textAfter.length && textAfter[offsetAfter] !== ' ') {
        const spaceAfter = document.createTextNode(' ');
        range.insertNode(spaceAfter);
        range.setStartAfter(spaceAfter);
        range.collapse(true);
      } else if (offsetAfter === textAfter.length) {
        // If at the very end, always add a space to make it easier to type after
        const spaceAfter = document.createTextNode(' ');
        range.insertNode(spaceAfter);
        range.setStartAfter(spaceAfter);
        range.collapse(true);
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Get the new text value after insertion (to sync with parent)
      const newText = this.getTextContent(this.$refs.editorRef);
      
      // Sync the value (only if it's a string and not an event object)
      if (newText !== this.lastValue && typeof newText === 'string' && newText !== '[object InputEvent]' && !newText.includes('[object')) {
        this.lastValue = newText;
        
        // Temporarily block updates from the parent while we're emitting
        this.isInternalUpdate = true;
        this.$emit('input', newText);
        this.$emit('change', newText);
        
        // Release the block in the next tick
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
    },
    insertTextAtCursor(text) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Sync value will handle emission and isInternalUpdate
      this.syncValue();
    },
    positionCursorAtPoint(x, y) {
      if (!this.$refs.editorRef) return;
      
      try {
        // Use caretPositionFromPoint for modern browsers, fallback to caretRangeFromPoint
        let range = null;
        if (document.caretPositionFromPoint) {
          const pos = document.caretPositionFromPoint(x, y);
          if (pos) {
            range = document.createRange();
            range.setStart(pos.offsetNode, pos.offset);
            range.collapse(true);
          }
        } else if (document.caretRangeFromPoint) {
          range = document.caretRangeFromPoint(x, y);
        }
        
        if (range) {
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (e) {
        console.warn('Error positioning cursor:', e);
      }
    },
    handleInsertField(event) {
      const { fieldKey, targetFieldId, dropX, dropY } = event.detail;
      
      if (targetFieldId === this.id && this.$refs.editorRef) {
        // Focus the editor first
        this.$refs.editorRef.focus();
        
        // Position cursor at drop coordinates if provided
        if (dropX !== undefined && dropY !== undefined) {
          this.positionCursorAtPoint(dropX, dropY);
        }
        
        // Insert the field immediately (synchronously) so buttons render right away
        this.insertFieldAtCursor(`{${fieldKey}}`);
      }
    },
    trackMousePosition(event) {
      // Store mouse position for cursor positioning
      if (event.dataTransfer && event.dataTransfer.effectAllowed === 'move') {
        this.currentDragX = event.clientX;
        this.currentDragY = event.clientY;
      }
    },
    syncValue() {
      if (!this.$refs.editorRef) return;
      
      const text = this.getTextContent(this.$refs.editorRef);
      // Filter out '[object InputEvent]' and any other object strings
      if (text !== this.lastValue && typeof text === 'string' && text !== '[object InputEvent]' && !text.includes('[object')) {
        this.lastValue = text;
        
        // Temporarily block updates from the parent while we're emitting
        this.isInternalUpdate = true;
        this.$emit('input', text);
        this.$emit('change', text);
        
        // Release the block in the next tick
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
    }
  }
};
</script>

<style scoped>
.rich-text-editor {
  width: 100%;
  min-height: 80px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  outline: none;
  font-size: 14px;
  line-height: 1.5;
  overflow-y: auto;
  word-wrap: break-word;
  white-space: pre-wrap; /* Preserve line breaks and wrap text */
}

.rich-text-editor.single-line {
  min-height: 36px !important;
  height: 36px;
  padding: 0 10px !important;
  white-space: nowrap;
  overflow: hidden;
  display: block;
  line-height: 34px;
}

.rich-text-editor.single-line :deep(.merge-field-button) {
  margin-top: 0;
  margin-bottom: 0;
  vertical-align: middle;
  line-height: normal;
}

.rich-text-editor:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.rich-text-editor.drag-over {
  border-color: #3b82f6;
  background-color: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.rich-text-editor:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}

.rich-text-editor :deep(.merge-field-button) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  margin: 0 2px;
  background: white;
  color: #24acfe;
  border: 1px solid #24acfe;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  cursor: grab;
  user-select: none;
  transition: all 0.2s;
  line-height: 1.2;
  min-height: 18px;
  min-width: 20px;
  vertical-align: middle;
}

.rich-text-editor :deep(.merge-field-button:hover) {
  background: #f0f9ff;
  box-shadow: 0 2px 4px rgba(36, 172, 254, 0.1);
}

.rich-text-editor :deep(.merge-field-button:active) {
  cursor: grabbing;
}
</style>

